import { useState, useEffect, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useBookingStore } from '../store/bookingStore';
import { useAvailabilityQuery } from './useAvailabilityQuery';
import { formatISO } from '../utils/dateUtils';
import { checkTimeSlotAvailability } from '../services/availability';

/**
 * Hook personalizado para gerenciar a seleção de data, horário e profissional
 * 
 * @param {string} salonId - ID do salão
 * @param {Object} options - Opções adicionais
 * @param {Date} options.initialDate - Data inicial opcional
 * @param {string} options.initialTime - Horário inicial opcional
 * @param {Object} options.initialProfessional - Profissional inicial opcional
 * @param {string} options.excludeAppointmentId - ID do agendamento a excluir da verificação (para remarcações)
 * @returns {Object} - Funções e estados para manipular a seleção de data, hora e profissional
 */
export const useDateTimeSelection = (salonId, options = {}) => {
  const {
    initialDate = null,
    initialTime = null,
    initialProfessional = null,
    excludeAppointmentId = null
  } = options;

  // Log para depuração da inicialização 
  console.log(`[useDateTimeSelection] Inicializado com salonId=${salonId}, excludeAppointmentId=${excludeAppointmentId}`);
  console.log('[useDateTimeSelection] Opções:', {
    initialDate: initialDate ? (typeof initialDate === 'object' ? initialDate.toISOString() : initialDate) : null,
    initialTime,
    initialProfessional: initialProfessional ? 
      (typeof initialProfessional === 'object' ? initialProfessional.id : initialProfessional) : null,
    excludeAppointmentId
  });

  // Form context do React Hook Form
  const { watch, setValue, trigger } = useFormContext();
  const selectedServices = watch('services') || [];

  // Estado local para acompanhar seleções
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [selectedTime, setSelectedTime] = useState(initialTime);
  const [selectedProfessional, setSelectedProfessional] = useState(initialProfessional);

  // Estado do Zustand store
  const {
    date: storeDate,
    time: storeTime,
    professional: storeProfessional,
    setDate,
    setTime,
    setProfessional
  } = useBookingStore();

  // Hook de disponibilidade otimizado
  const {
    useAvailableTimeSlots,
    useDetailedTimeSlots,
    useAvailableProfessionals,
    useSlotStatus,
    prefetchAvailability,
    invalidateAvailability
  } = useAvailabilityQuery(salonId);

  // Usar valores iniciais do store, se disponíveis
  useEffect(() => {
    if (!selectedDate && storeDate) {
      setSelectedDate(storeDate);
    }
    if (!selectedTime && storeTime) {
      setSelectedTime(storeTime);
    }
    if (!selectedProfessional && storeProfessional) {
      setSelectedProfessional(storeProfessional);
    }
  }, [storeDate, storeTime, storeProfessional, selectedDate, selectedTime, selectedProfessional]);

  // Buscar horários disponíveis usando o hook otimizado
  const { 
    data: availableTimeSlots = [],
    isLoading: timeSlotsLoading
  } = useAvailableTimeSlots({
    date: selectedDate,
    services: selectedServices,
    excludeAppointmentId,
    enabled: !!selectedDate && selectedServices.length > 0
  });
  
  // Log para rastrear a consulta de horários disponíveis
  useEffect(() => {
    if (selectedDate && selectedServices.length > 0) {
      console.log(`[useDateTimeSelection] Consultando horários para data=${typeof selectedDate === 'object' ? 
        selectedDate.toISOString() : selectedDate}, servicos=${selectedServices.length}, excludeAppointmentId=${excludeAppointmentId}`);
      
      if (availableTimeSlots) {
        console.log(`[useDateTimeSelection] Horários retornados: ${availableTimeSlots.length}, carregando=${timeSlotsLoading}`);
      }
    }
  }, [selectedDate, selectedServices, availableTimeSlots, timeSlotsLoading, excludeAppointmentId]);
  
  // Buscar horários com informações detalhadas (incluindo bloqueios)
  const { 
    data: detailedTimeSlots = [],
    isLoading: detailedTimeSlotsLoading
  } = useDetailedTimeSlots({
    date: selectedDate,
    services: selectedServices,
    excludeAppointmentId,
    enabled: !!selectedDate && selectedServices.length > 0
  });

  // Log para rastrear a consulta de horários detalhados
  useEffect(() => {
    if (selectedDate && selectedServices.length > 0) {
      console.log(`[useDateTimeSelection] Consultando horários detalhados com excludeAppointmentId=${excludeAppointmentId}`);
      
      if (detailedTimeSlots) {
        console.log(`[useDateTimeSelection] Horários detalhados retornados: ${detailedTimeSlots.length}, carregando=${detailedTimeSlotsLoading}`);
        
        if (detailedTimeSlots.length === 0) {
          console.warn(`[useDateTimeSelection] ALERTA: Nenhum horário detalhado retornado para excludeAppointmentId=${excludeAppointmentId}`);
        }
      }
    }
  }, [selectedDate, selectedServices, detailedTimeSlots, detailedTimeSlotsLoading, excludeAppointmentId]);

  // Buscar profissionais disponíveis usando o hook otimizado
  const {
    data: availableProfessionals = [],
    isLoading: professionalsLoading
  } = useAvailableProfessionals({
    date: selectedDate,
    time: selectedTime,
    services: selectedServices,
    excludeAppointmentId,
    enabled: !!selectedDate && !!selectedTime && selectedServices.length > 0
  });
  
  // Verificar status do slot selecionado quando houver
  const {
    data: selectedSlotStatus = null,
    isLoading: slotStatusLoading
  } = useSlotStatus({
    date: selectedDate,
    time: selectedTime,
    professional: selectedProfessional,
    services: selectedServices,
    excludeAppointmentId,
    enabled: !!selectedDate && !!selectedTime && !!selectedProfessional && selectedServices.length > 0 && selectedProfessional !== null
  });

  // Handlers para alterações
  const handleDateChange = useCallback((date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setSelectedProfessional(null);
    
    console.log(`[useDateTimeSelection] Data alterada para ${date}, excludeAppointmentId=${excludeAppointmentId}`);
    
    // Atualizar form e store
    setValue('date', date, { shouldValidate: true });
    setValue('time', null);
    setValue('professional', null);
    setDate(date);
    setTime(null);
    setProfessional(null);
    
    // Pré-carregar dados de disponibilidade para a data selecionada
    if (date && selectedServices.length > 0) {
      console.log(`[useDateTimeSelection] Pré-carregando disponibilidade para data=${date} com excludeAppointmentId=${excludeAppointmentId}`);
      prefetchAvailability(date, selectedServices, excludeAppointmentId);
    }
  }, [setValue, setDate, setTime, setProfessional, selectedServices, prefetchAvailability, excludeAppointmentId]);

  const handleTimeSelect = useCallback((time) => {
    setSelectedTime(time);
    setSelectedProfessional(null);
    
    console.log(`[useDateTimeSelection] Horário selecionado: ${time}`);
    
    // Atualizar form e store
    setValue('time', time, { shouldValidate: true });
    setValue('professional', null);
    setTime(time);
    setProfessional(null);
  }, [setValue, setTime, setProfessional]);

  const handleProfessionalSelect = useCallback((professional) => {
    setSelectedProfessional(professional);
    
    console.log(`[useDateTimeSelection] Profissional selecionado: ${typeof professional === 'object' ? professional.id : professional}`);
    
    // Atualizar form e store
    setValue('professional', professional, { shouldValidate: true });
    setProfessional(professional);
  }, [setValue, setProfessional]);

  // Verificar se o botão continuar deve estar desabilitado
  const continueDisabled = !selectedDate || !selectedTime || !selectedProfessional;

  // Função para validar antes de continuar
  const validateAndContinue = async (onContinue) => {
    // Validar o formulário antes de avançar
    const isValid = await trigger(['date', 'time', 'professional']);
    
    console.log(`[useDateTimeSelection] Validando para continuar: valido=${isValid}, excludeAppointmentId=${excludeAppointmentId}`);
    
    // Verificar disponibilidade final quando temos todos os dados
    if (isValid && selectedDate && selectedTime && selectedProfessional) {
      // Verificar status atual do slot
      const professionalId = typeof selectedProfessional === 'object' ? selectedProfessional.id : selectedProfessional;
      const serviceIds = selectedServices.map(s => typeof s === 'object' ? s.id : s);
      
      console.log(`[useDateTimeSelection] Verificando disponibilidade final:`, {
        date: formatISO(selectedDate),
        time: selectedTime,
        professionalId,
        servicesCount: serviceIds.length,
        excludeAppointmentId
      });
      
      const currentStatus = await checkTimeSlotAvailability(
        salonId,
        formatISO(selectedDate),
        selectedTime,
        professionalId,
        serviceIds,
        excludeAppointmentId
      );
      
      console.log(`[useDateTimeSelection] Resultado da verificação:`, {
        available: currentStatus.available,
        hasBlockingConflicts: currentStatus.hasBlockingConflicts,
        hasAppointmentConflicts: currentStatus.hasAppointmentConflicts,
        conflicts: currentStatus.conflicts?.length || 0
      });
      
      // Se não estiver mais disponível, mostrar mensagem
      if (!currentStatus.available) {
        let message = "Este horário não está mais disponível.";
        
        if (currentStatus.hasBlockingConflicts) {
          message = "Este horário foi bloqueado pelo administrador.";
        } else if (currentStatus.hasAppointmentConflicts) {
          message = "Este horário foi reservado por outro cliente.";
        }
        
        // Mostrar alerta (pode ser substituído por um toast ou outro tipo de feedback)
        alert(message);
        
        // Forçar uma nova verificação de disponibilidade
        invalidateAvailability(formatISO(selectedDate));
        return false;
      }
    }
    
    if (isValid && onContinue) {
      onContinue();
    }
    return isValid;
  };

  // Estado de carregamento - não incluir slotStatusLoading quando não há profissional selecionado
  const isLoading = timeSlotsLoading || detailedTimeSlotsLoading || professionalsLoading || 
                    (!!selectedProfessional && slotStatusLoading);

  return {
    // Estados
    selectedDate,
    selectedTime,
    selectedProfessional,
    availableTimeSlots,
    detailedTimeSlots,
    availableProfessionals,
    selectedSlotStatus,
    isLoading,
    continueDisabled,
    
    // Handlers
    handleDateChange,
    handleTimeSelect,
    handleProfessionalSelect,
    validateAndContinue
  };
}; 